import type { QueryKey } from '@tanstack/react-query';

import { ENDPOINTS, type SearchPackageArchitecturesResponse } from '../../../../shared/registrySearch';
import { useQuery } from '../../../ReactQuery';
import { get } from '../../../redux/api';
import type { ThirdPartyFetcher } from '../../../../shared/fetchers';

const STALE_MS = 60_000;

const getQueryKey = (
  fetcher: ThirdPartyFetcher | undefined,
  packageName: string,
  packageVersion: string,
): QueryKey => ['registry-search', 'architectures', fetcher, packageName, packageVersion];

/**
 * Fetches the architecture list for a given package version from the registry-search facade. Used to power
 * the architecture dropdown in the Add Dependency drawer.
 *
 * Disabled until `fetcher`, `packageName` and `packageVersion` are set AND the caller signals via
 * `enabled`.
 */
export const useSearchPackageArchitectures = (
  fetcher: ThirdPartyFetcher | undefined,
  packageName: string,
  packageVersion: string,
  enabled: boolean = true,
) => {
  // Normalize at the fetch boundary: registry identifiers can't contain whitespace (the
  // registry-search validators reject it via SAFE_CHARS), so a whitespace-only package name
  // shouldn't fire a doomed lookup. Trimming here keeps every caller honest, not just this one.
  const trimmedPackage = packageName.trim();
  const trimmedPackageVersion = packageVersion.trim();

  return useQuery<SearchPackageArchitecturesResponse>({
    queryKey: getQueryKey(fetcher, trimmedPackage, trimmedPackageVersion),
    queryFn: () => get<SearchPackageArchitecturesResponse>(
      ENDPOINTS.SEARCH_PACKAGE_ARCHITECTURES,
      false,
      {
        fetcher: fetcher ?? '',
        package: trimmedPackage,
        version: trimmedPackageVersion,
      },
    ),
    enabled: enabled && !!fetcher && !!trimmedPackage && !!trimmedPackageVersion,
    staleTime: STALE_MS,
  });
};
